// src/components/RegisterPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../utils/auth.jsx";
import { Eye, EyeOff, Shield, Zap, Star, ArrowRight, DollarSign } from "lucide-react";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tier, setTier] = useState("observer");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get register function from auth context
  const { register, TIER_CONFIG } = useAuth();

  // Set tier from URL parameter if provided
  useEffect(() => {
    const tierFromUrl = searchParams.get('tier');
    if (tierFromUrl && TIER_CONFIG[tierFromUrl]) {
      setTier(tierFromUrl);
    }
  }, [searchParams, TIER_CONFIG]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      // Register with selected tier - this will auto-login
      await register(name, email, password, tier);
      
      // Redirect to Dashboard (user is now logged in)
      navigate("/", { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Define tier features matching pricing page
  const tierFeatures = {
    observer: [
      "10 website analyses per month",
      "Basic scoring and insights", 
      "Public feed access",
      "Community support",
      "Mobile-responsive reports"
    ],
    professional: [
      "Unlimited website analyses",
      "Advanced AI insights & recommendations",
      "API access (1,000 calls/month)",
      "Export reports & data",
      "Email alerts for opportunities",
      "Priority support response",
      "Competitive analysis tools"
    ],
    business: [
      "Everything in Professional",
      "White-label reports with your branding",
      "Team collaboration (10 seats)",
      "Custom integrations",
      "Dedicated account manager",
      "Priority analysis queue",
      "API access (10,000 calls/month)",
      "Advanced analytics dashboard"
    ],
    enterprise: [
      "Everything in Business",
      "Custom AI model training",
      "Unlimited API access",
      "Dedicated infrastructure",
      "SLA guarantees (99.9% uptime)",
      "Onboarding & training",
      "Unlimited team seats",
      "Compliance & security customization"
    ]
  };

  const tierDescriptions = {
    observer: "Get started with basic features",
    professional: "Everything you need to excel",
    business: "Scale your agency operations", 
    enterprise: "Custom solutions for large teams"
  };

  const getSelectedTierFeatures = () => {
    return tierFeatures[tier]?.slice(0, 4) || [];
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="bg-slate-800/60 backdrop-blur-2xl border border-slate-700/50 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 rounded-3xl" />
          
          <div className="relative z-10">
            {/* Enhanced Logo */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-600 rounded-2xl mb-6 shadow-lg shadow-blue-500/25 relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
                <Eye className="w-10 h-10 text-white relative z-10" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent mb-2">
                Create Account
              </h1>
              <p className="text-slate-400">Join CodeRated to start analyzing websites</p>
              
            </div>

            {/* Pricing CTA Banner - Prominent but not intrusive */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-sm font-semibold text-white">Compare All Plans</p>
                    <p className="text-xs text-slate-400">Find the perfect fit for your needs</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/pricing")}
                  className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors group"
                >
                  <span>View Pricing</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl backdrop-blur-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-400 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Register Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-200 mb-2">
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-slate-700/70 transition-all hover:bg-slate-700/60"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-200 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-slate-700/70 transition-all hover:bg-slate-700/60"
                  placeholder="Enter your email"
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-slate-200 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength="6"
                    className="w-full px-4 py-3.5 pr-12 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-slate-700/70 transition-all hover:bg-slate-700/60"
                    placeholder="Create a password (min. 6 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-200 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3.5 pr-12 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-slate-700/70 transition-all hover:bg-slate-700/60"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Enhanced Tier Selection */}
              <div>
                <label htmlFor="tier" className="block text-sm font-semibold text-slate-200 mb-3">
                  Select Your Plan
                </label>
                <select
                  id="tier"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="w-full px-4 py-3.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all hover:bg-slate-700/60"
                >
                  <option value="observer">
                    Observer - Free Forever
                  </option>
                  <option value="professional">
                    Professional - $49/month ⭐ MOST POPULAR
                  </option>
                  <option value="business">
                    Business - $149/month 💼 BEST VALUE  
                  </option>
                  <option value="enterprise">
                    Enterprise - Custom Pricing 👑
                  </option>
                </select>
                
                {/* Enhanced Plan Description */}
                <div className="mt-4 p-4 bg-slate-700/30 backdrop-blur-sm border border-slate-600/30 rounded-xl">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-blue-400 text-lg">
                        {TIER_CONFIG[tier]?.name || tier.charAt(0).toUpperCase() + tier.slice(1)} Plan
                      </div>
                      <div className="text-slate-300 text-sm mb-1">
                        {tierDescriptions[tier]}
                      </div>
                      <div className="text-slate-400 text-sm">
                        {tier === 'observer' && 'Free Forever'}
                        {tier === 'professional' && '$49/month'}
                        {tier === 'business' && '$149/month'}
                        {tier === 'enterprise' && 'Custom Pricing'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {tier === 'professional' && (
                        <div className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full text-center">
                          POPULAR
                        </div>
                      )}
                      {tier === 'business' && (
                        <div className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full text-center">
                          BEST VALUE
                        </div>
                      )}
                      {tier === 'enterprise' && (
                        <div className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full text-center">
                          ENTERPRISE
                        </div>
                      )}
                      {tier !== 'observer' && (
                        <div className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full text-center">
                          PREMIUM
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {getSelectedTierFeatures().map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-3 flex-shrink-0" />
                        <span className="text-slate-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                    {tierFeatures[tier]?.length > 4 && (
                      <div className="text-xs text-slate-500 mt-2">
                        +{tierFeatures[tier].length - 4} more features included
                      </div>
                    )}
                  </div>
                  
                  {/* Value Props */}
                  {tier === 'professional' && (
                    <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-blue-400 font-medium">
                        💡 Save 40+ hours/month • See ROI immediately
                      </p>
                    </div>
                  )}
                  {tier === 'business' && (
                    <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <p className="text-xs text-green-400 font-medium">
                        🚀 Perfect for agencies • Pays for itself with 2 clients
                      </p>
                    </div>
                  )}
                  {tier === 'enterprise' && (
                    <div className="mt-3 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                      <p className="text-xs text-purple-400 font-medium">
                        ⚡ Tailored to your needs • Full customization
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:via-cyan-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  <span className="flex items-center justify-center">
                    <Zap className="w-5 h-5 mr-2" />
                    Create {tier.charAt(0).toUpperCase() + tier.slice(1)} Account
                  </span>
                )}
              </button>
            </form>

            {/* Login Link */}
            <div className="mt-8 text-center">
              <p className="text-slate-400">
                Already have an account?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors hover:underline"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
        
        {/* Security & Trust Badges */}
        <div className="text-center mt-6 space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-full text-xs text-slate-400">
            <Shield className="w-3 h-3 text-green-400" />
            <span>256-bit SSL encryption</span>
          </div>
          <div className="text-xs text-slate-500">
            Free accounts include 10 analyses/month • Upgrade anytime • No credit card required
          </div>
        </div>
      </div>
    </div>
  );
}